import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode, useState} from 'react';

import {
  BaseChartParameters,
  XYChartParameters,
} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {getColumnOptionDatas} from 'insights/analytics-visualizations/configs/config-components/ConfigEditorUtils';
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {VisualizationConfigEditorComponentProps} from 'insights/analytics-visualizations/configs/VisualizationConfigEditorTypes';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHTER} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import ErrorBarColumnEditor from './ErrorBarColumnEditor';
import SortDirectionConfigEditor from './SortDirectionConfigEditor';

export type XYSeriesColumnEditorProps = VisualizationConfigEditorComponentProps<
  BaseChartParameters & {errorBarColumnName?: string | null}
> & {
  isSortable?: boolean;
  enableErrorBarColumn?: boolean;
};

export default function XYSeriesColumnEditor(props: XYSeriesColumnEditorProps): JSX.Element {
  return (
    <>
      <XYColumnEditor {...props} />
      <SeriesColumnEditor {...props} />
      {props.enableErrorBarColumn && <ErrorBarColumnEditor {...props} />}
    </>
  );
}

export type SeriesParameter = Pick<BaseChartParameters, 'seriesColumnName'>;

export function SeriesColumnEditor({
  columns,
  onUpdateParameters,
  parameters,
  errorMessage = null,
  useSystemNames,
  isDisabled = false,
}: VisualizationConfigEditorComponentProps<SeriesParameter> & {
  errorMessage?: string | null;
}): JSX.Element {
  function handleSelectSeriesColumnName(selectedValue: string): void {
    onUpdateParameters({
      seriesColumnName: selectedValue,
    });
  }

  return (
    <>
      <div className="u-input-spacing-top">
        <FieldLabel label="Series" />
        <DropdownSelector
          allowNull={true}
          errorMessage={errorMessage}
          onChange={handleSelectSeriesColumnName}
          searchable={true}
          selectedValue={parameters.seriesColumnName}
          optionDatas={getColumnOptionDatas(columns, useSystemNames)}
          disabled={isDisabled}
        />
      </div>
    </>
  );
}

function AxisEditorSubMenu({children}: {children: ReactNode}): JSX.Element {
  return <div className={css(styles.axisSubOptionsContainer)}>{children}</div>;
}

export function XYColumnEditor(
  props: VisualizationConfigEditorComponentProps<XYChartParameters> & {
    fieldLabels?: {x: string; y: string};
    reverseDisplayedAxes?: boolean;
    isSortable?: boolean;
  }
): JSX.Element {
  const {
    columns,
    fieldLabels = {x: 'X-Axis', y: 'Y-Axis'},
    onUpdateParameters,
    reverseDisplayedAxes = false,
    isSortable = false,
    parameters,
    useSystemNames,
    isDisabled = false,
  } = props;

  const [isXAxisSubmenuOpen, setIsXAxisSubmenuOpen] = useState(false);

  function handleSelectXAxisColumnName(selectedValue: string): void {
    onUpdateParameters({
      xAxisColumnName: selectedValue,
    });
  }
  function handleSelectYAxisColumnName(selectedValue: string): void {
    onUpdateParameters({
      yAxisColumnName: selectedValue,
    });
  }

  function handleSwapAxisColumnNames(): void {
    onUpdateParameters({
      xAxisColumnName: parameters.yAxisColumnName,
      yAxisColumnName: parameters.xAxisColumnName,
    });
  }

  const columnOptionDatas = getColumnOptionDatas(columns, useSystemNames);

  const xAxisSelector = isSortable ? (
    <>
      <DropdownSelector
        allowNull={false}
        onChange={handleSelectXAxisColumnName}
        searchable={true}
        selectedValue={parameters.xAxisColumnName}
        optionDatas={columnOptionDatas}
        settingsIconProps={{
          isActive: isXAxisSubmenuOpen,
          onClick: () => {
            setIsXAxisSubmenuOpen(!isXAxisSubmenuOpen);
          },
        }}
        disabled={isDisabled}
      />
      {isXAxisSubmenuOpen && (
        <AxisEditorSubMenu>
          <SortDirectionConfigEditor {...props} />
        </AxisEditorSubMenu>
      )}
    </>
  ) : (
    <DropdownSelector
      allowNull={false}
      onChange={handleSelectXAxisColumnName}
      searchable={true}
      selectedValue={parameters.xAxisColumnName}
      optionDatas={columnOptionDatas}
      disabled={isDisabled}
    />
  );

  const yAxisSelector = (
    <DropdownSelector
      allowNull={false}
      onChange={handleSelectYAxisColumnName}
      searchable={true}
      selectedValue={parameters.yAxisColumnName}
      optionDatas={columnOptionDatas}
      disabled={isDisabled}
    />
  );

  return (
    <>
      <div className="u-input-spacing-top">
        <FieldLabel label={reverseDisplayedAxes ? fieldLabels.y : fieldLabels.x} />
        {reverseDisplayedAxes ? yAxisSelector : xAxisSelector}
      </div>
      <div className="u-input-spacing-top">
        <div className="u-lr-split">
          <FieldLabel label={reverseDisplayedAxes ? fieldLabels.x : fieldLabels.y} />
          <div className={css(styles.swapButton)}>
            <Button
              icon="arrow-up-down"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              onClick={handleSwapAxisColumnNames}
              dataTestAttrs={{component: 'XYSeriesColumnEditor', element: 'swapButton'}}
              disabled={isDisabled}
            />
          </div>
        </div>
        {reverseDisplayedAxes ? xAxisSelector : yAxisSelector}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  swapButton: {
    marginTop: -15,
  },
  xAxisSelectorGroup: {
    display: 'flex',
    flexDirection: 'row',
  },
  xAxisDropdownContainer: {
    flexGrow: 1,
  },
  axisSubOptionsContainer: {
    border: `1px solid ${GRAY}`,
    backgroundColor: GRAY_LIGHTER,
    padding: '6px',
    marginTop: '6px',
    borderRadius: '0 0 6px 6px',
  },
  axisSubMenuButton: {
    marginLeft: '6px',
  },
});
